import { useState, useEffect } from 'react';

const STORAGE_KEY = 'timesheet_layout';

/**
 * Detects whether the device is mobile and respects a manual user override.
 *
 * Returns:
 *   isMobile    — boolean: true when mobile layout is active
 *   isAuto      — boolean: true when following the auto-detected value (no override)
 *   toggleLayout — function: switch between mobile and desktop, stores preference
 *   resetAuto    — function: clear the override and go back to auto-detection
 */
function detectMobile() {
  // Treat as mobile when viewport width <= 768px OR the device is a touch device
  // with a narrow screen (phones/tablets in portrait).
  const byWidth = window.innerWidth <= 768;
  const byUA = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  return byWidth || byUA;
}

export default function useLayoutMode() {
  const [isMobile, setIsMobile] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'mobile') return true;
    if (saved === 'desktop') return false;
    return detectMobile(); // auto
  });

  const [isAuto, setIsAuto] = useState(() => {
    return !localStorage.getItem(STORAGE_KEY);
  });

  // Re-run auto-detection when the viewport is resized — only when there is
  // no manual override.
  useEffect(() => {
    if (!isAuto) return;

    const handler = () => setIsMobile(detectMobile());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [isAuto]);

  const toggleLayout = () => {
    const next = !isMobile;
    localStorage.setItem(STORAGE_KEY, next ? 'mobile' : 'desktop');
    setIsMobile(next);
    setIsAuto(false);
  };

  const resetAuto = () => {
    localStorage.removeItem(STORAGE_KEY);
    setIsMobile(detectMobile());
    setIsAuto(true);
  };

  return { isMobile, isAuto, toggleLayout, resetAuto };
}
